// Export utility functions

export function download(name: string, blob: Blob) {
  const a = document.createElement("a");
  const url = URL.createObjectURL(blob);
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function serializeSVG(svg: SVGSVGElement): string {
  // Ensure namespaces
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  svg.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  const serializer = new XMLSerializer();
  return serializer.serializeToString(svg);
}

function isLocalExport(): boolean {
  try {
    if (typeof window === "undefined") return false;
    const h = window.location.hostname || "";
    const p = window.location.protocol || "";
    return p === "file:" || h === "localhost" || h.startsWith("127.") || h === "::1";
  } catch (e) {
    return false;
  }
}

function serializeSVGForExport(svg: SVGSVGElement): string {
  // Clone the SVG so we don't mutate the live DOM
  const clone = svg.cloneNode(true) as SVGSVGElement;

  // Remove watermark elements (class 'print-only-watermark') for all exports
  try {
    const marks = clone.querySelectorAll('.print-only-watermark');
    marks.forEach((n) => n.remove());
  } catch (e) {}

  return serializeSVG(clone);
}

function safeGetSVG(): SVGSVGElement | null {
  const el = document.querySelector('#suzan-svg');
  if (!el) return null;
  // runtime guard: ensure it's an SVG element
  if (el instanceof SVGSVGElement) return el;
  return null;
}

export async function exportSVG(mode: string) {
  const svg = safeGetSVG();
  if (!svg) return;
  const str = serializeSVGForExport(svg);
  download(`suzan_${mode}.svg`, new Blob([str], { type: "image/svg+xml" }));
}

export async function exportRaster(mode: string, exportScale: number, solidBG: boolean, brandDeep: string, mime: string) {
  const svg = safeGetSVG();
  if (!svg) return;
  const w = 794, h = 1123;
  const str = serializeSVGForExport(svg);
  const url = URL.createObjectURL(new Blob([str], { type: "image/svg+xml" }));
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = w * exportScale;
    canvas.height = h * exportScale;
  const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (solidBG) {
      ctx.fillStyle = brandDeep;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) download(`suzan_${mode}_${exportScale}x.${mime === "image/png" ? "png" : "webp"}`, blob);
    }, mime, 0.95);
    URL.revokeObjectURL(url);
  };
  img.onerror = () => URL.revokeObjectURL(url);
  img.src = url;
}

export async function copyPNGToClipboard(exportScale: number, solidBG: boolean, brandDeep: string) {
  try {
    const svg = safeGetSVG();
    if (!svg) return;
    const w = 794, h = 1123;
  const str = serializeSVGForExport(svg);
  const url = URL.createObjectURL(new Blob([str], { type: "image/svg+xml" }));
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = w * exportScale;
        canvas.height = h * exportScale;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(null);
        if (solidBG) {
          ctx.fillStyle = brandDeep;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(async (blob) => {
          if (!blob) return resolve(null);
          // @ts-ignore ClipboardItem global
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          resolve(null);
        }, "image/png", 0.95);
        URL.revokeObjectURL(url);
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
    alert("PNG copied to clipboard");
  } catch (e) {
    alert("Clipboard copy failed. Your browser may block it.");
  }
}

export function copySVGCode() {
  const svg = safeGetSVG();
  if (!svg) return;
  const str = serializeSVG(svg);
  navigator.clipboard.writeText(str).then(() => alert("SVG code copied."));
}

export function getLatexSnippet(mode: string): string {
  return `\\begin{figure}[t]\n  \\centering\n  \\includegraphics[width=\\linewidth]{suzan_${mode}.svg}\n  \\caption{Suzan AI Cybersecurity Whitepaper — Cover (mode: ${mode}).}\n\\end{figure}`;
}
